import { BehaviorSubject } from 'bog-arcade-core-ui/observer/BehaviorSubject';

class GamePadService {
  constructor(
    {
      spin = false,
      autoSpinState = false,
      betAmountState = false,
      switchButtonState = false,
    } = {},
  ) {
    this._spin = new BehaviorSubject(spin);
    this._autoSpinState = new BehaviorSubject(autoSpinState);
    this._betAmountState = new BehaviorSubject(betAmountState);
    this._switchButtonState = new BehaviorSubject(switchButtonState);
    this._quickBets = new BehaviorSubject([]);
    this._quickBetsIndex = new BehaviorSubject(0);
    this._spinCounts = new BehaviorSubject([5, 10, 20, 50, 100, Infinity]);
    this._spinCount = new BehaviorSubject(0);
    this._spinCountIndex = new BehaviorSubject(0);
    this._quickBetsOpenedState = new BehaviorSubject(false);
    this._spinCountsOpenedState = new BehaviorSubject(false);
    this._autoSpinActiveState = new BehaviorSubject(false);
  }

  get spinState() {
    return this._spin.getValue();
  }

  get autoSpinState() {
    return this._autoSpinState.getValue();
  }

  get autoSpinActiveState() {
    return this._autoSpinActiveState.getValue();
  }

  get betAmountState() {
    return this._betAmountState.getValue();
  }

  get quickBet() {
    return this._quickBets.getValue()[this._quickBetsIndex.getValue()];
  }

  set quickBets(values) {
    return this._quickBets.next(values);
  }

  get quickBets() {
    return this._quickBets.getValue();
  }

  get spinCount() {
    return this._spinCounts.getValue()[this._spinCountIndex.getValue()];
  }

  get currentSpinCount() {
    return this._spinCount.getValue();
  }

  get spinCounts() {
    return this._spinCounts.getValue();
  }

  setSpinCount(value) {
    this._spinCount.next(value);
  }

  setAutoSpinActiveState(value) {
    this._autoSpinActiveState.next(value);
  }

  subscribeAutoSpinCount(callback, initialCall = true) {
    if (initialCall) {
      callback(this.spinCount);
    }
    const countsSubscriber = this._spinCounts.subscribe(() => callback(this.spinCount));
    const indexSubscriber = this._spinCountIndex.subscribe(() => callback(this.spinCount));
    return {
      unsubscribe: () => {
        countsSubscriber.unsubscribe();
        indexSubscriber.unsubscribe();
      },
    };
  }

  subscribeCurrentCountChange(callback, initialCall = true) {
    return this._spinCount.subscribe(callback, initialCall);
  }

  subscribeQuickBet(callback, initialCall = true) {
    if (initialCall) {
      callback(this.quickBet);
    }

    const quickSubscriber = this._quickBets.subscribe(() => callback(this.quickBet));
    const indexSubscriber = this._quickBetsIndex.subscribe(() => callback(this.quickBet));
    return {
      unsubscribe: () => {
        quickSubscriber.unsubscribe();
        indexSubscriber.unsubscribe();
      },
    };
  }

  subscribeSpinState(...args) {
    return this._spin.subscribe(...args);
  }

  subscribeAutoSpinState(...args) {
    return this._autoSpinState.subscribe(...args);
  }

  subscribeBetAmountState(...args) {
    return this._betAmountState.subscribe(...args);
  }

  subscribeQuickBetsIndex(callback, initialCall = true) {
    return this._quickBetsIndex.subscribe(callback, initialCall);
  }

  subscribeSpinCountIndex(callback, initialCall = true) {
    return this._spinCountIndex.subscribe(callback, initialCall);
  }

  prevQuickBet() {
    const current = this._quickBetsIndex.getValue();
    if (current !== 0) {
      this._quickBetsIndex.next(current - 1);
    }
  }

  nextQuickBet() {
    const current = this._quickBetsIndex.getValue();
    const { length } = this._quickBets.getValue();
    if (current < length - 1) {
      this._quickBetsIndex.next(current + 1);
    }
  }

  prevSpinCount() {
    const current = this._spinCountIndex.getValue();
    if (current !== 0) {
      this._spinCountIndex.next(current - 1);
    }
  }

  nextSpinCount() {
    const current = this._spinCountIndex.getValue();
    const { length } = this._spinCounts.getValue();
    if (current < length - 1) {
      this._spinCountIndex.next(current + 1);
    }
  }

  disableSpin() {
    this._spin.next(false);
  }

  enableSpin() {
    this._spin.next(true);
  }

  disableAutoSpinState() {
    this._autoSpinState.next(false);
  }

  enableAutoSpinState() {
    this._autoSpinState.next(true);
  }

  disableBetAmountState() {
    this._betAmountState.next(false);
  }

  enableBetAmountState() {
    this._betAmountState.next(true);
  }

  enableAll() {
    this.enableSpin();
    this.enableAutoSpinState();
    this.enableBetAmountState();
    this.enableSwitchButtonState();
  }

  disableAll() {
    this.disableSpin();
    this.disableAutoSpinState();
    this.disableBetAmountState();
    this.disableSwitchButtonState();
  }

  disableAllWhileAutoSpin() {
    this.disableAutoSpinState();
    this.disableBetAmountState();
    this.disableSwitchButtonState();
  }

  subscribeQuickBetsActiveState(callback, initialCall = true) {
    return this._quickBetsOpenedState.subscribe(callback, initialCall);
  }

  subscribeSpinCountsActiveState(callback, initialCall = true) {
    return this._spinCountsOpenedState.subscribe(callback, initialCall);
  }

  get spinCountsOpenedState() {
    return this._spinCountsOpenedState.getValue();
  }

  openSpinCount() {
    this._spinCountsOpenedState.next(true);
  }

  closeSpinCount() {
    this._spinCountsOpenedState.next(false);
  }

  get quickBetsOpenedState() {
    return this._quickBetsOpenedState.getValue();
  }

  openQuickBets() {
    this._quickBetsOpenedState.next(true);
  }

  closeQuickBets() {
    this._quickBetsOpenedState.next(false);
  }

  get switchButtonState() {
    return this._switchButtonState.getValue();
  }

  disableSwitchButtonState() {
    this._switchButtonState.next(false);
  }

  enableSwitchButtonState() {
    this._switchButtonState.next(true);
  }

  switchButtonSubscribe(callback, initialCall = true) {
    return this._switchButtonState.subscribe(callback, initialCall);
  }

  spinButtonSubscribe(callback, initialCall = true) {
    return this._spin.subscribe(callback, initialCall);
  }

  autoSpinSubscribe(callback, initialCall = true) {
    return this._autoSpinState.subscribe(callback, initialCall);
  }

  autoSpinButtonSubscribe(callback, initialCall = true) {
    return this._autoSpinActiveState.subscribe(callback, initialCall);
  }

  betAmountSubscribe(callback, initialCall = true) {
    return this._betAmountState.subscribe(callback, initialCall);
  }
}

export const gamePadService = new GamePadService({
  spin: true,
});
